/**
 * useEditableField — Contenteditable ↔ ref sync without cursor jumps
 *
 * Usage:
 *   const titleField = useEditableField(title, { placeholder: 'Title' });
 *   // In template:
 *   <div contenteditable="true"
 *        :data-placeholder="titleField.placeholder"
 *        @input="titleField.onInput"
 *        @paste="titleField.onPaste"
 *        @focus="titleField.onFocus"
 *        @blur="titleField.onBlur"
 *        :ref="titleField.setRef"
 *   ></div>
 *
 * Key design decisions:
 * - We do NOT use v-text or {{ }} on the contenteditable element.
 *   That would trigger Vue reactivity on every keystroke and reset cursor.
 * - Instead, @input reads el.innerText → ref (one-way: DOM → ref).
 * - sync() pushes ref → DOM (one-way: ref → DOM) for loadFromItem/reset.
 * - Paste is intercepted to insert plain text only (prevents XSS).
 */

import { ref } from "vue";

/**
 * @param {import('vue').Ref<string>} valueRef - The reactive ref to bind to
 * @param {Object} [options]
 * @param {string} [options.placeholder] - Placeholder text shown when empty
 * @param {Function} [options.onFocusCb] - Optional callback when field is focused
 * @param {Function} [options.onBlurCb] - Optional callback when field is blurred
 * @param {Function} [options.onPasteCb] - Optional callback receiving pasted text string
 * @returns {{ fieldRef, setRef, onInput, onPaste, onFocus, onBlur, sync, placeholder, isFocused }}
 */
export function useEditableField(valueRef, options = {}) {
  const { placeholder = "", onFocusCb, onBlurCb, onPasteCb } = options;
  const fieldRef = ref(null);
  const isFocused = ref(false);

  // Template ref setter — use as :ref="field.setRef"
  // Auto-populates the element with the current ref value on attach,
  // so content survives template branch switches and late mounts.
  const setRef = (el) => {
    const prev = fieldRef.value;
    // Guard: when Vue unmounts old branch AFTER mounting new branch,
    // the stale null callback must not clobber the freshly-attached ref.
    if (!el && prev && prev.isConnected) return;
    fieldRef.value = el;
    // Only populate when a NEW element is attached (not on every re-render)
    if (el && el !== prev && valueRef.value) {
      el.innerText = valueRef.value;
    }
  };

  // DOM → ref (user typing)
  const onInput = (e) => {
    valueRef.value = e.target.innerText || "";
  };

  // Intercept paste — plain text only
  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") || "";
    console.log("[useEditableField] onPaste fired", { text, hasOnPasteCb: !!onPasteCb });
    if (text) {
      // execCommand is deprecated but universally supported and simplest
      // for contenteditable plain-text insertion with undo support
      document.execCommand("insertText", false, text);
      // Notify caller of pasted text (e.g. for auto-detecting YouTube links)
      if (onPasteCb) onPasteCb(text);
    }
  };

  const onFocus = () => {
    isFocused.value = true;
    if (onFocusCb) onFocusCb();
  };

  const onBlur = () => {
    isFocused.value = false;
    if (onBlurCb) onBlurCb();
  };

  // ref → DOM (programmatic push, e.g. loadFromItem, resetDraft)
  const sync = () => {
    if (fieldRef.value) {
      fieldRef.value.innerText = valueRef.value || "";
    }
  };

  return {
    fieldRef,
    setRef,
    onInput,
    onPaste,
    onFocus,
    onBlur,
    sync,
    placeholder,
    isFocused,
  };
}

/**
 * Insert text at the current caret position in a contenteditable element.
 * Falls back to appending to a ref if no element is focused.
 *
 * @param {HTMLElement|null} el - The contenteditable element
 * @param {string} text - The text to insert (e.g. emoji)
 * @param {import('vue').Ref<string>} [fallbackRef] - Fallback ref to append to
 * @param {Function} [fallbackSync] - Fallback sync function to push ref → DOM
 */
export function insertTextAtCaret(el, text, fallbackRef, fallbackSync) {
  if (!el) {
    if (fallbackRef) {
      fallbackRef.value = (fallbackRef.value || "") + text;
      if (fallbackSync) fallbackSync();
    }
    return;
  }
  el.focus();
  document.execCommand("insertText", false, text);
}
